import type { ProductResponse, GroupedRelationshipResponse } from './product.types';
import type { DimensionAssetMap, FamilyAssetMap } from './familyAssetMap.types';
import { buildProduct3dConfig } from './product3dConfig';

export const PRODUCT_IMAGE_FALLBACK =
	'https://content.directsupplycdn.com/CE769534DF39B1AA494759C95A6C6BD6C5333FB59B54E8C71AF44C7780B55D14';

const MARKETING_API_BASE_URL = process.env.MARKETING_API_BASE_URL ?? '';

const productCache = new Map<string, ProductResponse>();
const familyAssetMapCache = new Map<string, FamilyAssetMap>();
const dimensionAssetMapCache = new Map<string, DimensionAssetMap>();
const dimensionValueAssetMapCache = new Map<string, DimensionAssetMap>();

export function getCachedProduct(
	productId: string,
): ProductResponse | undefined {
	return productCache.get(productId);
}

export function getCachedFamilyAssetMap(
	familyId: string,
): FamilyAssetMap | undefined {
	return familyAssetMapCache.get(familyId);
}

/**
 * Cache key for a dimension asset map. The Maxwell-Thomas-proper set is a
 * distinct response for the same slug, so it must not collide with the full set.
 */
function dimensionAssetMapCacheKey(slug: string, maxwellThomasProperOnly: boolean): string {
	return maxwellThomasProperOnly ? `${slug}?maxwellThomasProperOnly=true` : slug;
}

export function getCachedDimensionAssetMap(
	slug: string,
	maxwellThomasProperOnly = false,
): DimensionAssetMap | undefined {
	return dimensionAssetMapCache.get(dimensionAssetMapCacheKey(slug, maxwellThomasProperOnly));
}

export class ProductApiError extends Error {
	public status: number;

	constructor(message: string, status: number) {
		super(message);
		this.name = 'ProductApiError';
		this.status = status;
	}
}

/**
 * Fetches a product by its 5-character product identifier.
 * Returns a cached result if available.
 *
 * @throws {ProductApiError} on non-2xx responses
 */
export async function getProductById(
	productId: string,
): Promise<ProductResponse> {
	const cached = productCache.get(productId);
	if (cached) return cached;

	const url = `${MARKETING_API_BASE_URL}/v1/products/${encodeURIComponent(productId)}`;

	const response = await fetch(url);

	if (!response.ok) {
		throw new ProductApiError(
			`Failed to fetch product "${productId}": ${response.status} ${response.statusText}`,
			response.status,
		);
	}

	const product = (await response.json()) as ProductResponse;
	
	if (!product.config3d) {
		product.config3d = buildProduct3dConfig(product);
	}

	if (!product.config3d) {
		product.config3d = buildProduct3dConfig(product.assets);
	}

	if ((!product.images || product.images.length === 0) && !product.config3d) {
		product.images = [{ contentUrl: PRODUCT_IMAGE_FALLBACK }];
	}

	productCache.set(productId, product);

	return product;
}

export async function getFamilyAssetMap(
	familyId: string,
): Promise<FamilyAssetMap> {
	const cached = familyAssetMapCache.get(familyId);
	if (cached) return cached;

	const url = `${MARKETING_API_BASE_URL}/v1/assets/families/${encodeURIComponent(familyId)}/asset-map`;

	const response = await fetch(url);

	if (!response.ok) {
		throw new ProductApiError(
			`Failed to fetch family asset map "${familyId}": ${response.status} ${response.statusText}`,
			response.status,
		);
	}

	const map = (await response.json()) as FamilyAssetMap;

	if (map.id) {
		familyAssetMapCache.set(map.id, map);
	}
	familyAssetMapCache.set(familyId, map);

	return map;
}

/**
 * Fetches the asset map for a single dimension slug (e.g. "seat-fabric"),
 * returning every value available for that dimension independent of any
 * product family. Returns a cached result if available.
 *
 * When `maxwellThomasProperOnly` is true the request adds `?maxwellThomasProperOnly=true`,
 * returning only the hand-curated "Maxwell Thomas proper" subset of values
 * (used on non-quick-ship product pages). The response shape is unchanged; it
 * simply contains fewer values. Defaults to false = today's full set.
 *
 * @throws {ProductApiError} on non-2xx responses
 */
export async function getDimensionAssetMap(
	slug: string,
	maxwellThomasProperOnly = false,
): Promise<DimensionAssetMap> {
	const cacheKey = dimensionAssetMapCacheKey(slug, maxwellThomasProperOnly);
	const cached = dimensionAssetMapCache.get(cacheKey);
	if (cached) return cached;

	const url = `${MARKETING_API_BASE_URL}/v1/assets/dimensions/${encodeURIComponent(slug)}/asset-map${maxwellThomasProperOnly ? '?maxwellThomasProperOnly=true' : ''}`;

	const response = await fetch(url);

	if (!response.ok) {
		throw new ProductApiError(
			`Failed to fetch dimension asset map "${slug}": ${response.status} ${response.statusText}`,
			response.status,
		);
	}

	const map = (await response.json()) as DimensionAssetMap;

	dimensionAssetMapCache.set(cacheKey, map);

	return map;
}

/**
 * Fetches the asset map for a single dimension VALUE (e.g. dimension
 * "frame-finish", value "walnut"), independent of any product family. Used to
 * resolve a fixed finish for a product that has no family asset map but names
 * the finish in its own attributes (see `materialAttributeSelections`).
 *
 * Returns the same {@link DimensionAssetMap} shape as the whole-dimension
 * endpoint, scoped to the one requested value. Returns a cached result if
 * available.
 *
 * @throws {ProductApiError} on non-2xx responses
 */
export async function getDimensionValueAssetMap(
	dimensionSlug: string,
	valueSlug: string,
): Promise<DimensionAssetMap> {
	const cacheKey = `${dimensionSlug}/${valueSlug}`;
	const cached = dimensionValueAssetMapCache.get(cacheKey);
	if (cached) return cached;

	const url = `${MARKETING_API_BASE_URL}/v1/assets/dimensions/${encodeURIComponent(dimensionSlug)}/option-values/${encodeURIComponent(valueSlug)}/asset-map`;

	const response = await fetch(url);

	if (!response.ok) {
		throw new ProductApiError(
			`Failed to fetch dimension value asset map "${dimensionSlug}/${valueSlug}": ${response.status} ${response.statusText}`,
			response.status,
		);
	}

	const map = (await response.json()) as DimensionAssetMap;

	dimensionValueAssetMapCache.set(cacheKey, map);

	return map;
}

export async function getProductRelationships(
	productId: string,
	productType: string,
): Promise<GroupedRelationshipResponse> {
	const url = `${MARKETING_API_BASE_URL}/v1/products/${encodeURIComponent(productId)}/relationships/grouped?productType=${encodeURIComponent(productType)}`;

	const response = await fetch(url);

	if (!response.ok) {
		throw new ProductApiError(
			`Failed to fetch relationships for "${productId}": ${response.status} ${response.statusText}`,
			response.status,
		);
	}

	return (await response.json()) as GroupedRelationshipResponse;
}
